import { useState } from "react";
import { client } from "../net/client";
import { guestSeat, type RoomInvite } from "../net/invite";
import { useSignal } from "../net/signal";
import { faNum } from "../game/cards";

/**
 * حساب در صفحه‌ی بازی — فقط «وارد شده‌ای یا نه».
 *
 * فرمِ ثبت‌نام/ورود صفحه‌ی جداگانه‌ی سایت است (`/account.html` از
 * `site/build.ts` + `site/auth.js`)، نه این‌جا: بازی سبک می‌مانَد و متن و توضیحِ حساب جایش در صفحه‌ی معرفی است.
 * این‌جا فقط می‌پرسیم ثبت‌شده است، و اگر نه، می‌فرستیمش آن‌جا و برمی‌گردانیم.
 *
 * بازی حساب نمی‌خواهد (کاربر، ۲۶ اکتبر ۲۰۲۶: «باید بشود بی ورود بازی کرد و اتاق ساخت»): بازیِ سریع، ربات،
 * ساختن و پیوستنِ اتاق برای مهمان هم باز است و مهمان همان دمِ اولین «بازی» ساخته می‌شود (client.seat).
 * حساب برای چیزهایی است که بی آن از دست می‌روند: میزِ ورودی‌دار، خرید، اشتراک، گردونه و جام — مهمان
 * به شناسه‌ی این مرورگر بسته است و پاک‌کردنِ دادهٔ سایت یعنی سکه و انبار رفته.
 */

export type AuthMode = "register" | "login" | "password";

/** حسابِ فعال؟ = ثبت‌شده و ایمیلش تأییدشده. نامِ خالی یعنی مهمان؛ نامِ پُر و
    verified نادرست یعنی هنوز کدِ ایمیل را نزده. */
export function useRegistered(): boolean {
  const username = useSignal(client.username);
  const verified = useSignal(client.verified);
  return username !== "" && verified;
}

/**
 * نشانیِ صفحه‌ی حساب (`/account.html`، ساخته‌ی site/build.ts) با برگشت به همین‌جا.
 *
 * `next` مسیرِ فعلی با query است تا لینکِ اتاقِ دوست (`?room=`) از دست نرود:
 * بعدِ ثبت‌نام همان‌جا برمی‌گردد و می‌پیوندد.
 */
export function authHref(mode: AuthMode): string {
  const next = typeof location !== "undefined" ? location.pathname + location.search : "/play";
  return "/account.html?auth=" + mode + "&next=" + encodeURIComponent(next);
}

/**
 * دروازه‌ی لینکِ اتاقِ دوست (docs/friend-invite.md)، جای دکمه‌های بازی برای کسی که حسابِ فعال ندارد: چه کسی او
 * را به کدام میز خوانده، «شروع» ِ بی‌حساب ([[GuestSeat]]) و راهِ حساب. میزِ ورودی‌دار «شروع» ندارد — سرور مهمان
 * را سرِ آن نمی‌نشاند — و فقط ورود می‌خواهد.
 *
 * ثبت‌شده‌ی تأییدنشده هم مثلِ مهمان می‌نشیند؛ پایینِ دروازه به جای «ثبت‌نام»، «تأییدِ ایمیل» می‌بیند.
 */
export function InviteGate({ room }: { room: RoomInvite }) {
  const username = useSignal(client.username);
  const staked = (room.stake ?? 0) > 0;
  return (
    <div className="plate stack gate">
      <InviteHead room={room} />
      {staked ? <StakeNote room={room} /> : <GuestSeat room={room} />}
      {username ? (
        <a className="btn ghost" href={authHref("login")}>تأیید ایمیل</a>
      ) : (
        <>
          {!staked && <AccountPitch room={room} />}
          <div className="row" style={{ gap: 8 }}>
            <a className="btn" style={{ flex: 1 }} href={authHref("register")}>ثبت‌نام</a>
            <a className="btn ghost" style={{ flex: 1 }} href={authHref("login")}>حساب دارم — ورود</a>
          </div>
        </>
      )}
    </div>
  );
}

/** میزِ ورودی‌دارِ دوست: حساب می‌خواهد، چون سکه‌ای که سرِ میز می‌رود باید با نام و رمز برگردد. */
function StakeNote({ room }: { room: RoomInvite }) {
  return (
    <div className="muted">
      این میز ورودی {faNum(room.stake ?? 0)} سکه دارد؛ برای نشستن سر میز ورودی‌دار باید وارد شوی. میز رایگان را خودت بی حساب بچین و لینکش را بفرست.
    </div>
  );
}

/** کدِ لینک درست بود ولی اتاق دیگر نیست — بالای دکمه‌های بازی، تا بازیکن بداند چرا سرِ آن میز نرفت. */
export function InviteGone() {
  return (
    <div className="muted">
      میزی که به آن دعوت شدی دیگر باز نیست؛ شاید بازی شروع شده یا میزبان رفته. از دوستت بخواه دوباره دعوتت کند —
      یا خودت میز بچین و لینکش را بفرست.
    </div>
  );
}

/**
 * سرِ دروازه برای کسی که با لینکِ اتاقِ دوستی آمده (docs/friend-invite.md): چه کسی او را به کدام میز خوانده، و
 * این‌که همین حالا بی حساب می‌نشیند ([[GuestSeat]]) — جز سرِ میزِ ورودی‌دار.
 *
 * نامِ میزبان در <bdi>: نامِ لاتین («masoud») وسطِ جمله‌ی فارسی ترتیبِ واژه‌ها را به هم می‌زند.
 */
function InviteHead({ room }: { room: RoomInvite }) {
  const game = room.game_fa || "بازی";
  return (
    <>
      <b>
        {room.host ? <><bdi>{room.host}</bdi> تو را به میز {game} دعوت کرده</> : <>دعوت به میز {game}</>}
      </b>
      {!(room.stake && room.stake > 0) && (
        <div className="muted">همین حالا بنشین — حساب لازم نیست. یک اسم بگذار تا بقیه‌ی میز بشناسندت.</div>
      )}
    </>
  );
}

/**
 * «شروع» بی حساب (کاربر، ۲۵ اکتبر ۲۰۲۶؛ نامِ دکمه پیش‌تر «بنشین سرِ میز» بود): یک نام (اختیاری، سقفِ ۱۶ مثلِ سرور) و یک دکمه. مهمان همان دم
 * ساخته می‌شود و App همان اتاقِ نشانی را می‌پیوندد ([[guestSeat]]، client.seatAsGuest). نامِ موقتِ سرور («مهمان
 * ۱۲۳۴») پیش‌نویس نمی‌شود. میزِ پر دکمه‌ی خاموش دارد.
 */
function GuestSeat({ room }: { room: RoomInvite }) {
  const current = useSignal(client.playerName);
  const going = useSignal(guestSeat);
  const [name, setName] = useState(TEMP_NAME.test(current) ? "" : current);
  const full = room.taken >= room.seats;
  const go = async () => {
    await client.seatAsGuest(name);
    guestSeat.value = true;
  };
  return (
    <form className="stack guestseat" onSubmit={(e) => { e.preventDefault(); if (!full && !going) void go(); }}>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={16} placeholder="اسمت (اختیاری)"
             aria-label="اسمت سر میز" autoComplete="nickname" enterKeyHint="go" />
      <button className="btn bigbtn" type="submit" disabled={full || going}>
        {full ? "میز پر است" : going ? "در حال شروع…" : "شروع"}
      </button>
    </form>
  );
}

/** نامِ موقتِ مهمان — همان قاعده‌ی سرور (store.AdoptNameIfTemporary) و اپ (IsGuestName). */
const TEMP_NAME = /^(مهمان|Guest) [0-9۰-۹]{4}$/;

/**
 * راهِ دومِ دروازه‌ی لینکِ اتاق: حسابِ رایگان، تا سکه‌ها بمانند — و هدیه‌ی دعوت، که فقط با حساب و بعد از اولین بازیِ
 * تمام‌شده به هر دو می‌رسد (docs/friend-invite.md)، نه سرِ ثبت‌نام.
 */
function AccountPitch({ room }: { room: RoomInvite }) {
  return (
    <>
      <div className="or"><span>یا با حساب رایگان، تا سکه‌هایت بمانند</span></div>
      {room.coins > 0 && (
        <div className="gate-gift">
          🎁 با حساب، بعد از اولین بازی‌ات، تو{room.host ? <> و <bdi>{room.host}</bdi></> : null} هر کدام {faNum(room.coins)} سکه هدیه می‌گیرید.
        </div>
      )}
    </>
  );
}
